import { Controller } from "@hotwired/stimulus";
import backs from "../presentation/backs";
import chooser from "../presentation/chooser";
import drag from "../presentation/drag";
import hiding from "../presentation/hiding";
import keyboard from "../presentation/keyboard";
import layout from "../presentation/layout";
import menu from "../presentation/menu";
import navigation from "../presentation/navigation";
import notes from "../presentation/notes";
import overview from "../presentation/overview";
import phone from "../presentation/phone";
import photos from "../presentation/photos";
import remote from "../presentation/remote";
import screen from "../presentation/screen";
import touch from "../presentation/touch";

// Slides are 960×540 boards. The canvas holding them is drawn at that size
// and scaled as one piece to fit the stage, so a slide looks the same in a
// meeting room as on a laptop instead of reflowing per screen.
//
// Three ways to look at a deck:
//   - one board at a time (the default), scaled to the stage;
//   - the overview (O), every board in a grid, click one to go there, or
//     hide one or drag it to a new place;
//   - on a phone held upright (phone.js), each board a page of its own:
//     the headline and Saga's read at a phone's size, the visual under them;
//   - stacked, on a narrow screen asked for the board view by name: every
//     board in a column at the width of the screen, pinch to zoom.
//
// And the presenter's phone (remote), which is not a way to look at the
// deck but to drive it: the board on show, small, the notes, Back and Next.
//
// The controller keeps what every part shares: the targets and values, the
// listeners set up on connect and taken down on disconnect, and showSlide,
// which every way through the deck ends in. The parts are in
// app/javascript/presentation/, one module each.
export default class PresentationController extends Controller {
  static targets = [
    "slide",
    "stage",
    "canvas",
    "counter",
    "progress",
    "chapter",
    "overviewButton",
    "prevButton",
    "nextButton",
    "notesPanel",
    "notesButton",
    "notes",
    "nextUp",
    "clock",
    "shareUrl",
    "theme",
    "fonts",
    "ownNote",
    "ownNoteText",
    "ownNoteInput",
    "ownNoteButton",
    "elapsed",
    "screenButton",
    "followButton",
    "chooser",
    "chooserList",
    "chooserChapters",
    "chooserKeep",
    "chooserKeepNote",
    "chooserSave",
    "chooserSaid",
    "turnButton",
    "notesBack",
    "overviewBar",
    "overviewCount",
    "overviewSaid",
    "segment",
    "chaptersSheet",
    "chapterCount",
  ];
  static values = {
    index: { type: Number, default: 0 },
    channel: { type: String, default: "" },
    presenter: { type: Boolean, default: false },
    remote: { type: Boolean, default: false },
    reportId: { type: Number, default: 0 },
    user: { type: Number, default: 0 },
    boardNotes: { type: Object, default: {} },
    choose: { type: Boolean, default: false },
    titles: { type: Object, default: {} },
    chapters: { type: Object, default: {} },
    phone: { type: Boolean, default: false },
  };

  connect() {
    this.totalSlides = this.slideTargets.length;
    this.indexBoards();

    this.stacked = this.shouldStack();
    this.overview = false;
    this.notesOpen = this.presenterValue || this.remoteValue;
    this.notesBySlug = { ...this.boardNotesValue };
    this.fit();

    // The presenter window and the room's window share a channel: whichever
    // turns the page, both move.
    if (this.channelValue && "BroadcastChannel" in window) {
      this.channel = new BroadcastChannel(this.channelValue);
      this.channel.onmessage = (event) => {
        const index = this.indexFor(event.data || {});
        if (index !== undefined && index !== this.indexValue) this.showSlide(index, true, false);
        if (typeof event.data?.turned === "boolean") this.turnTo(event.data);
      };
    }
    // The same over the cable, which also reaches the presenter's phone.
    // Only the presenter's own windows carry a report id; a share link is
    // not on the cable.
    if (this.reportIdValue) this.subscribe();
    this.followOthers = this.readFollow();
    this.renderFollow();
    if (this.presenterValue) {
      this.startClock();
      this.element.classList.add("deck--presenter");
    }
    if (this.remoteValue) {
      this.startClock();
      this.boundVisibility = () => document.visibilityState === "visible" && this.keepAwake();
      document.addEventListener("visibilitychange", this.boundVisibility);
      this.keepAwake();
    }

    // Read initial slide from URL hash — accepts either a semantic slug
    // (#revenue, #forecast, …) or the legacy positional form (#slide-3).
    let initial = this.slideFromHash();
    initial = this.nearestVisible(initial);
    this.showSlide(initial, false, false);
    this.ready = true;
    if (this.chooseValue) this.openChooser();

    this.boundKeydown = this.handleKeydown.bind(this);
    this.boundHashChange = this.handleHashChange.bind(this);
    this.boundPointerDown = this.handlePointerDown.bind(this);
    this.boundPointerUp = this.handlePointerUp.bind(this);
    this.boundPick = this.handlePick.bind(this);
    this.boundFullscreenChange = this.handleFullscreenChange.bind(this);
    document.addEventListener("keydown", this.boundKeydown);
    document.addEventListener("fullscreenchange", this.boundFullscreenChange);
    window.addEventListener("hashchange", this.boundHashChange);

    if (this.hasStageTarget) {
      this.stageTarget.addEventListener("pointerdown", this.boundPointerDown);
      this.stageTarget.addEventListener("pointerup", this.boundPointerUp);
      if ("ResizeObserver" in window) {
        this.resizeObserver = new ResizeObserver(() => this.fit());
        this.resizeObserver.observe(this.stageTarget);
      }
    }
    if (this.hasCanvasTarget) {
      this.canvasTarget.addEventListener("click", this.boundPick);
    }
    this.connectPhone();
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundKeydown);
    document.removeEventListener("fullscreenchange", this.boundFullscreenChange);
    window.removeEventListener("hashchange", this.boundHashChange);
    if (this.hasStageTarget) {
      this.stageTarget.removeEventListener(
        "pointerdown",
        this.boundPointerDown,
      );
      this.stageTarget.removeEventListener("pointerup", this.boundPointerUp);
    }
    if (this.hasCanvasTarget) {
      this.canvasTarget.removeEventListener("click", this.boundPick);
    }
    this.resizeObserver?.disconnect();
    this.disconnectPhone();
    this.channel?.close();
    this.subscription?.then((subscription) => subscription?.unsubscribe());
    this.flushNote();
    // A drag still in hand leaves its document listeners and its scroll
    // frame running after the deck has gone.
    this.releaseDrag();
    clearInterval(this.clockTimer);
    if (this.boundVisibility) document.removeEventListener("visibilitychange", this.boundVisibility);
    this.wakeLock?.release().catch(() => {});
  }

  // Where each board is, by its slug, for the address and the other
  // windows. Again whenever the boards change places in the overview.
  indexBoards() {
    this.slugByIndex = this.slideTargets.map(
      (slide) => slide.dataset.slideSlug || null,
    );
    this.indexBySlug = new Map();
    this.slugByIndex.forEach((slug, i) => {
      if (slug && !this.indexBySlug.has(slug)) this.indexBySlug.set(slug, i);
    });
  }

  // The board on show is marked `.is-current`, and that class is what the
  // deck's motion hangs off (deck/motion.css): a short fade on the
  // board, its bars rising and lines drawing once. Stacked, every board
  // stays on the page and the current one scrolls into view.
  showSlide(index, updateHash = true, broadcast = true) {
    if (this.ready && !this.startedAt && index !== this.indexValue) this.startedAt = Date.now();
    // Another board comes up the right way round.
    if (index !== this.indexValue && this.turned) {
      this.slideTargets.forEach((board) => board.classList.remove("is-turned"));
      this.turned = false;
    }
    this.indexValue = index;
    this.hydrate(index);
    if (broadcast) {
      const slug = this.slugByIndex[index] || undefined;
      this.channel?.postMessage({ index, slug });
      this.send({ index, slug });
    }

    this.slideTargets.forEach((slide, i) => {
      const current = i === index;
      if (this.stacked) {
        slide.style.visibility = "";
        slide.style.zIndex = "";
        slide.removeAttribute("aria-hidden");
      } else {
        slide.style.visibility = current ? "visible" : "hidden";
        slide.style.zIndex = current ? "1" : "0";
        slide.setAttribute("aria-hidden", current ? "false" : "true");
      }
      slide.classList.toggle("is-current", current);
    });

    if (this.stacked && updateHash) {
      this.slideTargets[index]?.scrollIntoView({
        block: "start",
        behavior: "smooth",
      });
    }

    // Update URL hash without triggering hashchange loop — prefer the
    // semantic slug so deep-links stay stable across report types.
    if (updateHash) {
      history.replaceState(null, "", `#${this.slideRef(index)}`);
    }

    // The count and the progress are over the boards the room will see.
    const visible = this.visibleIndices();
    const position = Math.max(visible.indexOf(index), 0);
    if (this.hasCounterTarget) {
      const pad = (n) => String(n).padStart(2, "0");
      this.counterTarget.textContent = `${pad(position + 1)} / ${pad(visible.length)}`;
    }

    if (this.hasProgressTarget) {
      const share =
        visible.length > 1 ? (position / (visible.length - 1)) * 100 : 100;
      this.progressTarget.style.setProperty(
        "--deck-progress",
        `${share.toFixed(2)}%`,
      );
    }

    this.renderNotes();
    this.renderTurn();

    const chapter = this.slideTargets[index]?.dataset.chapter;
    this.chapterTargets.forEach((button) => {
      const current = button.dataset.chapter === chapter;
      button.classList.toggle("is-current", current);
      if (current) button.setAttribute("aria-current", "true");
      else button.removeAttribute("aria-current");
    });

    if (this.hasPrevButtonTarget) {
      this.prevButtonTarget.disabled = position === 0;
    }
    if (this.hasNextButtonTarget) {
      this.nextButtonTarget.disabled = position === visible.length - 1;
    }
    if (this.phoneValue) this.renderPhone(index);
  }
}

// Each part is a module of methods written against this controller, as
// they were when it was one 1,500-line class: `this` is the controller, so
// a part reads the targets, the values and the other parts' methods as it
// always did, and an action in a view finds its method here. A name two
// parts define, or one Stimulus already does, would quietly replace the
// other; it fails as the page loads instead.
function compose(controller, parts) {
  parts.forEach((part) => {
    Object.entries(Object.getOwnPropertyDescriptors(part)).forEach(([name, descriptor]) => {
      if (name in controller.prototype) throw new Error(`presentation: ${name} is defined twice`);
      Object.defineProperty(controller.prototype, name, { ...descriptor, enumerable: false });
    });
  });
}

compose(PresentationController, [layout, keyboard, touch, navigation, hiding, overview, screen, notes, remote, backs, chooser, drag, photos, menu, phone]);
